import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckSquare, FiPlus, FiSquare, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import useCatalogAccess from "../../purchasing/components/useCatalogAccess";

const BULK_NO_CHANGE = "__no_change__";

const EMPTY_PRODUCT_FILTERS = {
  family_id: "",
  subfamily_id: "",
  category_id: "",
  subcategory_id: "",
  vat_rate_percent: "",
};

const VAT_OPTIONS = [
  { value: "", label: "IVA pending" },
  { value: "0", label: "0%" },
  { value: "4", label: "4%" },
  { value: "10", label: "10%" },
  { value: "21", label: "21%" },
];

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id;
}

function optionLabel(row) {
  return row ? `${row.code || row.id} - ${row.name}` : "";
}

function vatSelectValue(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "";
}

function initialBulkPatch() {
  return {
    family_id: BULK_NO_CHANGE,
    subfamily_id: BULK_NO_CHANGE,
    category_id: BULK_NO_CHANGE,
    subcategory_id: BULK_NO_CHANGE,
    vat_rate_percent: BULK_NO_CHANGE,
  };
}

function initialNewProduct() {
  return {
    plu: "",
    name: "",
    family_id: "",
    subfamily_id: "",
    category_id: "",
    subcategory_id: "",
    vat_rate_percent: "",
  };
}

export default function Products() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const access = useCatalogAccess(businessUnitId);
  const canUseProducts = access.can([]);
  const canViewRecipeLinks = access.can("purchasing.recipes.view");
  const renderStamp = access.capture();
  const [storedProducts, setProducts] = useState([]);
  const [storedCount, setCount] = useState(0);
  const [loadedContext, setLoadedContext] = useState("");
  const [editorContext, setEditorContext] = useState("");
  const products = useMemo(() => loadedContext === access.contextKey ? storedProducts : [], [loadedContext, access.contextKey, storedProducts]);
  const count = loadedContext === access.contextKey ? storedCount : 0;
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [uncategorized, setUncategorized] = useState(false);
  const [productFilters, setProductFilters] = useState(EMPTY_PRODUCT_FILTERS);
  const [storedTaxonomy, setTaxonomy] = useState({ families: [], subfamilies: [], categories: [], subcategories: [] });
  const [taxonomyContext, setTaxonomyContext] = useState("");
  const taxonomy = taxonomyContext === access.contextKey ? storedTaxonomy : { families: [], subfamilies: [], categories: [], subcategories: [] };
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState(initialNewProduct);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);
  const [bulkPatch, setBulkPatch] = useState(initialBulkPatch);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const limit = 50;

  const hasPrevious = offset > 0;
  const hasNext = offset + limit < count;
  const visibleProductIds = useMemo(() => products.map((product) => product.id), [products]);
  const selectedCount = loadedContext === access.contextKey ? (allFilteredSelected ? count : selectedIds.size) : 0;
  const selectionLabel = allFilteredSelected ? `${count} filtered selected` : `${selectedCount} selected`;
  const hasProductFilters =
    uncategorized ||
    Object.values(productFilters).some((value) => value !== null && value !== undefined && String(value).trim());

  const loadTaxonomy = useCallback(async () => {
    if (!access.can([])) return;
    const stamp = access.capture();
    try {
      const res = await axios.get("/api/pos/catalog/taxonomy", access.requestConfig([]));
      if (access.isCurrent(stamp, [])) {
        setTaxonomy(res.data || { families: [], subfamilies: [], categories: [], subcategories: [] });
        setTaxonomyContext(access.contextKey);
      }
    } catch (err) {
      if (access.isCurrent(stamp, [])) setError(err?.response?.data?.error || "Could not load taxonomy.");
    }
  }, [access]);

  const loadProducts = useCallback(async (nextOffset = 0) => {
    if (!access.can([])) return;
    const stamp = access.capture();
    try {
      setLoading(true);
      setError("");
      const config = access.requestConfig([]);
      const res = await axios.get("/api/pos/products", {
        ...config,
        params: {
          ...config.params,
          q: debouncedSearchQuery,
          uncategorized,
          ...productFilters,
          limit,
          offset: nextOffset,
        },
      });
      if (!access.isCurrent(stamp, [])) return;
      setProducts(res.data?.rows || []);
      setLoadedContext(access.contextKey);
      setCount(res.data?.count || 0);
      setOffset(nextOffset);
    } catch (err) {
      if (access.isCurrent(stamp, [])) setError(err?.response?.data?.error || "Could not load POS products.");
    } finally {
      if (access.isCurrent(stamp, [])) setLoading(false);
    }
  }, [access, debouncedSearchQuery, productFilters, uncategorized]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
  }, [businessUnitId, debouncedSearchQuery, productFilters, uncategorized]);

  useEffect(() => {
    setProducts([]);
    setLoadedContext("");
    setTaxonomyContext("");
    setEditorContext("");
    setTaxonomy({ families: [], subfamilies: [], categories: [], subcategories: [] });
    setCount(0);
    setOffset(0);
    setShowCreateProductModal(false);
    setShowBulkEditModal(false);
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
    setSavingId(null);
    setCreatingProduct(false);
    setBulkSaving(false);
    setLoading(false);
    setError("");
    setBulkMessage("");
  }, [access.contextKey]);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  useEffect(() => {
    loadProducts(0);
  }, [loadProducts]);

  const subfamiliesByFamily = useMemo(() => {
    const map = new Map();
    taxonomy.subfamilies.forEach((row) => {
      const key = String(row.family_id || "");
      map.set(key, [...(map.get(key) || []), row]);
    });
    return map;
  }, [taxonomy.subfamilies]);

  const categoriesBySubfamily = useMemo(() => {
    const map = new Map();
    taxonomy.categories.forEach((row) => {
      const key = String(row.subfamily_id || "");
      map.set(key, [...(map.get(key) || []), row]);
    });
    return map;
  }, [taxonomy.categories]);

  const subcategoriesByCategory = useMemo(() => {
    const map = new Map();
    taxonomy.subcategories.forEach((row) => {
      const key = String(row.category_id || "");
      map.set(key, [...(map.get(key) || []), row]);
    });
    return map;
  }, [taxonomy.subcategories]);

  const bulkSubfamilies = useMemo(() => {
    if (!bulkPatch.family_id || bulkPatch.family_id === BULK_NO_CHANGE) return taxonomy.subfamilies;
    return subfamiliesByFamily.get(String(bulkPatch.family_id)) || [];
  }, [bulkPatch.family_id, subfamiliesByFamily, taxonomy.subfamilies]);

  const bulkCategories = useMemo(() => {
    if (!bulkPatch.subfamily_id || bulkPatch.subfamily_id === BULK_NO_CHANGE) return taxonomy.categories;
    return categoriesBySubfamily.get(String(bulkPatch.subfamily_id)) || [];
  }, [bulkPatch.subfamily_id, categoriesBySubfamily, taxonomy.categories]);

  const bulkSubcategories = useMemo(() => {
    if (!bulkPatch.category_id || bulkPatch.category_id === BULK_NO_CHANGE) return taxonomy.subcategories;
    return subcategoriesByCategory.get(String(bulkPatch.category_id)) || [];
  }, [bulkPatch.category_id, subcategoriesByCategory, taxonomy.subcategories]);

  const filterSubfamilies = useMemo(() => {
    if (!productFilters.family_id) return taxonomy.subfamilies;
    return subfamiliesByFamily.get(String(productFilters.family_id)) || [];
  }, [productFilters.family_id, subfamiliesByFamily, taxonomy.subfamilies]);

  const filterCategories = useMemo(() => {
    if (!productFilters.subfamily_id) return taxonomy.categories;
    return categoriesBySubfamily.get(String(productFilters.subfamily_id)) || [];
  }, [categoriesBySubfamily, productFilters.subfamily_id, taxonomy.categories]);

  const filterSubcategories = useMemo(() => {
    if (!productFilters.category_id) return taxonomy.subcategories;
    return subcategoriesByCategory.get(String(productFilters.category_id)) || [];
  }, [productFilters.category_id, subcategoriesByCategory, taxonomy.subcategories]);

  const newProductSubfamilies = useMemo(() => {
    if (!newProduct.family_id) return taxonomy.subfamilies;
    return subfamiliesByFamily.get(String(newProduct.family_id)) || [];
  }, [newProduct.family_id, subfamiliesByFamily, taxonomy.subfamilies]);

  const newProductCategories = useMemo(() => {
    if (!newProduct.subfamily_id) return taxonomy.categories;
    return categoriesBySubfamily.get(String(newProduct.subfamily_id)) || [];
  }, [categoriesBySubfamily, newProduct.subfamily_id, taxonomy.categories]);

  const newProductSubcategories = useMemo(() => {
    if (!newProduct.category_id) return taxonomy.subcategories;
    return subcategoriesByCategory.get(String(newProduct.category_id)) || [];
  }, [newProduct.category_id, subcategoriesByCategory, taxonomy.subcategories]);

  const updateProductFilter = (field, value) => {
    setProductFilters((current) => ({
      ...current,
      [field]: value,
      ...(field === "family_id" ? { subfamily_id: "", category_id: "", subcategory_id: "" } : {}),
      ...(field === "subfamily_id" ? { category_id: "", subcategory_id: "" } : {}),
      ...(field === "category_id" ? { subcategory_id: "" } : {}),
    }));
  };

  const clearProductFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setUncategorized(false);
    setProductFilters(EMPTY_PRODUCT_FILTERS);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setAllFilteredSelected(false);
  };

  const openCreateProductModal = () => {
    if (!access.isCurrent(renderStamp, [])) return;
    setEditorContext(access.contextKey);
    setError("");
    setBulkMessage("");
    setNewProduct(initialNewProduct());
    setShowCreateProductModal(true);
  };

  const closeCreateProductModal = () => {
    if (creatingProduct) return;
    setShowCreateProductModal(false);
  };

  const updateNewProductTaxonomy = (field, value) => {
    setNewProduct((current) => ({
      ...current,
      [field]: value,
      ...(field === "family_id" ? { subfamily_id: "", category_id: "", subcategory_id: "" } : {}),
      ...(field === "subfamily_id" ? { category_id: "", subcategory_id: "" } : {}),
      ...(field === "category_id" ? { subcategory_id: "" } : {}),
    }));
  };

  const createProduct = async (event) => {
    event.preventDefault();
    if (!access.isCurrent(renderStamp, []) || editorContext !== access.contextKey) return;
    const stamp = access.capture();
    const plu = newProduct.plu.trim();
    const name = newProduct.name.trim();
    if (!plu) return setError("PLU is required.");
    if (!name) return setError("Product name is required.");

    try {
      setCreatingProduct(true);
      setError("");
      setBulkMessage("");
      await axios.post("/api/pos/products", {
        business_unit_id: businessUnitId,
        plu,
        name,
        family_id: newProduct.family_id || null,
        subfamily_id: newProduct.subfamily_id || null,
        category_id: newProduct.category_id || null,
        subcategory_id: newProduct.subcategory_id || null,
        vat_rate_percent: newProduct.vat_rate_percent === "" ? null : Number(newProduct.vat_rate_percent),
      }, access.requestConfig([]));
      if (!access.isCurrent(stamp, [])) return;
      setShowCreateProductModal(false);
      setNewProduct(initialNewProduct());
      setBulkMessage(`Product ${plu} created.`);
      await loadProducts(0);
    } catch (err) {
      if (access.isCurrent(stamp, [])) setError(err?.response?.data?.error || "Could not create POS product.");
    } finally {
      if (access.isCurrent(stamp, [])) setCreatingProduct(false);
    }
  };

  const selectVisible = () => {
    setSelectedIds(new Set(visibleProductIds));
    setAllFilteredSelected(false);
  };

  const selectAllFiltered = () => {
    if (!count) return;
    setSelectedIds(new Set());
    setAllFilteredSelected(true);
  };

  const openBulkEditModal = () => {
    if (!access.isCurrent(renderStamp, []) || loadedContext !== access.contextKey) return;
    setEditorContext(access.contextKey);
    setError("");
    setBulkMessage("");
    setShowBulkEditModal(true);
  };

  const closeBulkEditModal = () => {
    if (bulkSaving) return;
    setShowBulkEditModal(false);
  };

  const toggleVisibleSelection = () => {
    if (allFilteredSelected) {
      clearSelection();
      return;
    }
    const allVisibleSelected = visibleProductIds.length > 0 && visibleProductIds.every((id) => selectedIds.has(id));
    if (allVisibleSelected) {
      setSelectedIds((current) => {
        const next = new Set(current);
        visibleProductIds.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelectedIds((current) => {
      const next = new Set(current);
      visibleProductIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleProductSelection = (productId) => {
    if (allFilteredSelected) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const buildBulkPayloadPatch = () => {
    const patch = {};
    const hasFamily = bulkPatch.family_id !== BULK_NO_CHANGE;
    const hasSubfamily = bulkPatch.subfamily_id !== BULK_NO_CHANGE;
    const hasCategory = bulkPatch.category_id !== BULK_NO_CHANGE;
    const hasSubcategory = bulkPatch.subcategory_id !== BULK_NO_CHANGE;

    if (hasFamily) patch.family_id = bulkPatch.family_id || null;
    if (hasSubfamily) patch.subfamily_id = bulkPatch.subfamily_id || null;
    if (hasCategory) patch.category_id = bulkPatch.category_id || null;
    if (hasSubcategory) patch.subcategory_id = bulkPatch.subcategory_id || null;
    if (bulkPatch.vat_rate_percent !== BULK_NO_CHANGE) {
      patch.vat_rate_percent = bulkPatch.vat_rate_percent === "" ? null : Number(bulkPatch.vat_rate_percent);
    }

    return patch;
  };

  const updateProduct = async (product, patch) => {
    if (!access.isCurrent(renderStamp, []) || loadedContext !== access.contextKey) return;
    const stamp = access.capture();
    try {
      setSavingId(product.id);
      setError("");
      setBulkMessage("");
      const res = await axios.patch(`/api/pos/products/${product.id}`, {
        ...patch,
        business_unit_id: businessUnitId,
      }, access.requestConfig([]));
      if (!access.isCurrent(stamp, [])) return;
      setProducts((current) => current.map((row) => (row.id === product.id ? res.data : row)));
      await loadProducts(offset);
    } catch (err) {
      if (access.isCurrent(stamp, [])) setError(err?.response?.data?.error || "Could not update POS product.");
    } finally {
      if (access.isCurrent(stamp, [])) setSavingId(null);
    }
  };

  const applyBulkEdit = async () => {
    if (!access.isCurrent(renderStamp, []) || loadedContext !== access.contextKey || editorContext !== access.contextKey) return;
    const stamp = access.capture();
    if (!selectedCount) {
      setError("Select products before applying a bulk edit.");
      return;
    }

    const patch = buildBulkPayloadPatch();
    if (!Object.keys(patch).length) {
      setError("Choose at least one bulk field to update.");
      return;
    }

    try {
      setBulkSaving(true);
      setError("");
      setBulkMessage("");
      const res = await axios.patch("/api/pos/products/bulk", {
        business_unit_id: businessUnitId,
        scope: allFilteredSelected ? "filtered" : "ids",
        product_ids: allFilteredSelected ? undefined : Array.from(selectedIds),
        filters: {
          q: debouncedSearchQuery,
          uncategorized,
          ...productFilters,
        },
        patch,
      }, access.requestConfig([]));
      if (!access.isCurrent(stamp, [])) return;
      const updatedCount = Number(res.data?.updated_count || 0);
      setBulkMessage(`${updatedCount} products updated.`);
      setBulkPatch(initialBulkPatch());
      clearSelection();
      await loadProducts(offset);
      setShowBulkEditModal(false);
    } catch (err) {
      if (access.isCurrent(stamp, [])) setError(err?.response?.data?.error || "Could not update POS products.");
    } finally {
      if (access.isCurrent(stamp, [])) setBulkSaving(false);
    }
  };

  if (!canUseProducts) return <div role="alert" className="posStateBanner">Select an active business unit for this session to view POS products.</div>;

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">POS products</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading products..." : `${count} products in the POS product master`}
            </div>
          </div>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreateProductModal}>
              <FiPlus /> Add product
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {bulkMessage ? <div className="posInfoBanner">{bulkMessage}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">POS product master</h3>
          <div className="rm-rates-subtitle">Products created manually or imported from POS PLUs</div>
        </div>

        <div className="posProductControlBar">
          <div className="posProductFilters">
            <input
              className="rm-searchInput posProductSearchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search PLU or name"
            />
            <select
              className="posSelect"
              value={productFilters.family_id}
              onChange={(event) => updateProductFilter("family_id", event.target.value)}
            >
              <option value="">All families</option>
              {taxonomy.families.map((row) => (
                <option key={row.id} value={row.id}>{optionLabel(row)}</option>
              ))}
            </select>
            <select
              className="posSelect"
              value={productFilters.subfamily_id}
              onChange={(event) => updateProductFilter("subfamily_id", event.target.value)}
            >
              <option value="">All subfamilies</option>
              {filterSubfamilies.map((row) => (
                <option key={row.id} value={row.id}>{optionLabel(row)}</option>
              ))}
            </select>
            <select
              className="posSelect"
              value={productFilters.category_id}
              onChange={(event) => updateProductFilter("category_id", event.target.value)}
            >
              <option value="">All categories</option>
              {filterCategories.map((row) => (
                <option key={row.id} value={row.id}>{optionLabel(row)}</option>
              ))}
            </select>
            <select
              className="posSelect"
              value={productFilters.subcategory_id}
              onChange={(event) => updateProductFilter("subcategory_id", event.target.value)}
            >
              <option value="">All subcategories</option>
              {filterSubcategories.map((row) => (
                <option key={row.id} value={row.id}>{optionLabel(row)}</option>
              ))}
            </select>
            <select
              className="posSelect posProductVatFilter"
              value={productFilters.vat_rate_percent}
              onChange={(event) => updateProductFilter("vat_rate_percent", event.target.value)}
            >
              <option value="">All IVA</option>
              <option value="__pending__">IVA pending</option>
              {VAT_OPTIONS.filter((option) => option.value !== "").map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              type="button"
              className={`rm-topBtn ${uncategorized ? "rm-topBtn--amber" : ""}`}
              onClick={() => setUncategorized((prev) => !prev)}
            >
              Uncategorized
            </button>
            <button
              type="button"
              className="rm-topBtn"
              onClick={clearProductFilters}
              disabled={!hasProductFilters && !searchQuery}
            >
              <FiX /> Clear filters
            </button>
          </div>

          <div className="posProductSelectionActions">
            <div className="posBulkStatus posBulkStatus--compact">
              <strong>{selectionLabel}</strong>
              <span>{allFilteredSelected ? "Matching filters" : "Manual selection"}</span>
            </div>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={selectVisible} disabled={!products.length}>
              <FiCheckSquare /> Visible
            </button>
            <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={selectAllFiltered} disabled={!count}>
              <FiCheckSquare /> All filtered
            </button>
            <button type="button" className="rm-topBtn" onClick={clearSelection} disabled={!selectedCount}>
              <FiX /> Clear
            </button>
            <button
              type="button"
              className="rm-topBtn rm-topBtn--green"
              onClick={openBulkEditModal}
              disabled={!selectedCount}
            >
              Bulk edit
            </button>
          </div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading products..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + products.length)} of ${count}`}
          </small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={!hasPrevious} onClick={() => loadProducts(Math.max(0, offset - limit))}>
              Previous
            </button>
            <button type="button" className="rm-topBtn" disabled={!hasNext} onClick={() => loadProducts(offset + limit)}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "100px", "--rm-table-min-width": "1480px" }}>
            <colgroup>
              <col style={{ width: "52px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "240px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "110px" }} />
              {canViewRecipeLinks && <col style={{ width: "120px" }} />}
            </colgroup>
            <thead>
              <tr>
                <th className="res-th">
                  <button type="button" className="res-thBtn posSelectionHeader" onClick={toggleVisibleSelection}>
                    {allFilteredSelected || visibleProductIds.some((id) => selectedIds.has(id)) ? <FiCheckSquare /> : <FiSquare />}
                  </button>
                </th>
                <th className="res-th rm-sticky-1"><div className="res-thBtn"><span className="res-thLabel">PLU</span><span className="res-thSub">Source code</span></div></th>
                <th className="res-th rm-sticky-2"><div className="res-thBtn"><span className="res-thLabel">Name</span><span className="res-thSub">POS product</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Family</span><span className="res-thSub">Optional mapping</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Subfamily</span><span className="res-thSub">Optional mapping</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Category</span><span className="res-thSub">Optional mapping</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Subcategory</span><span className="res-thSub">Optional mapping</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">IVA</span><span className="res-thSub">Net calc</span></div></th>
                {canViewRecipeLinks && <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Cost</span><span className="res-thSub">Recipe link</span></div></th>}
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const familySubfamilies = subfamiliesByFamily.get(String(product.family_id || "")) || taxonomy.subfamilies;
                const subfamilyCategories = categoriesBySubfamily.get(String(product.subfamily_id || "")) || taxonomy.categories;
                const categorySubcategories =
                  subcategoriesByCategory.get(String(product.category_id || "")) || taxonomy.subcategories;
                const rowSelected = allFilteredSelected || selectedIds.has(product.id);
                return (
                  <tr key={product.id}>
                    <td className="posSelectionCell">
                      <input
                        type="checkbox"
                        className="posRowCheckbox"
                        checked={rowSelected}
                        disabled={allFilteredSelected}
                        onChange={() => toggleProductSelection(product.id)}
                        aria-label={`Select ${product.name}`}
                      />
                    </td>
                    <td className="rm-sticky-1">{product.plu}</td>
                    <td className="rm-sticky-2">{product.name}</td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={product.family_id || ""}
                        disabled={savingId === product.id}
                        onChange={(event) =>
                          updateProduct(product, {
                            family_id: event.target.value || null,
                            subfamily_id: null,
                            category_id: null,
                            subcategory_id: null,
                          })
                        }
                      >
                        <option value="">-</option>
                        {taxonomy.families.map((row) => (
                          <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={product.subfamily_id || ""}
                        disabled={savingId === product.id}
                        onChange={(event) =>
                          updateProduct(product, {
                            subfamily_id: event.target.value || null,
                            category_id: null,
                            subcategory_id: null,
                          })
                        }
                      >
                        <option value="">-</option>
                        {familySubfamilies.map((row) => (
                          <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={product.category_id || ""}
                        disabled={savingId === product.id}
                        onChange={(event) =>
                          updateProduct(product, {
                            category_id: event.target.value || null,
                            subcategory_id: null,
                          })
                        }
                      >
                        <option value="">-</option>
                        {subfamilyCategories.map((row) => (
                          <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={product.subcategory_id || ""}
                        disabled={savingId === product.id}
                        onChange={(event) => updateProduct(product, { subcategory_id: event.target.value || null })}
                      >
                        <option value="">-</option>
                        {categorySubcategories.map((row) => (
                          <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={vatSelectValue(product.vat_rate_percent)}
                        disabled={savingId === product.id}
                        onChange={(event) => updateProduct(product, { vat_rate_percent: event.target.value === "" ? null : Number(event.target.value) })}
                      >
                        {VAT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    {canViewRecipeLinks && <td>
                      {product.costing_status ? <span className={`rm-chip ${product.costing_status === "recipe_linked" ? "rm-chip--ok" : "rm-chip--soft"}`}>
                        {product.costing_status}
                      </span> : "—"}
                    </td>}
                  </tr>
                );
              })}
              {!products.length ? (
                <tr>
                  <td colSpan={canViewRecipeLinks ? 9 : 8} className="posMuted">No products found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateProductModal && editorContext === access.contextKey ? (
        <div className="posModalOverlay">
          <form className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-label="Add POS product" onSubmit={createProduct}>
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">Add POS product</h3>
                <div className="rm-rates-subtitle">Manual product master entry</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeCreateProductModal} disabled={creatingProduct} aria-label="Close product popup">x</button>
            </div>

            {error ? <div className="posStateBanner">{error}</div> : null}

            <div className="posProductModalBody">
              <div className="posModalFormGrid">
                <label className="posModalField">
                  <span>PLU</span>
                  <input className="posTextInput" value={newProduct.plu} onChange={(event) => setNewProduct((current) => ({ ...current, plu: event.target.value }))} maxLength={120} autoFocus />
                </label>
                <label className="posModalField">
                  <span>Product name</span>
                  <input className="posTextInput" value={newProduct.name} onChange={(event) => setNewProduct((current) => ({ ...current, name: event.target.value }))} maxLength={255} />
                </label>
                <label className="posModalField">
                  <span>Family</span>
                  <select className="posSelect" value={newProduct.family_id} onChange={(event) => updateNewProductTaxonomy("family_id", event.target.value)}>
                    <option value="">Unassigned</option>
                    {taxonomy.families.map((row) => <option key={row.id} value={row.id}>{optionLabel(row)}</option>)}
                  </select>
                </label>
                <label className="posModalField">
                  <span>Subfamily</span>
                  <select className="posSelect" value={newProduct.subfamily_id} onChange={(event) => updateNewProductTaxonomy("subfamily_id", event.target.value)}>
                    <option value="">Unassigned</option>
                    {newProductSubfamilies.map((row) => <option key={row.id} value={row.id}>{optionLabel(row)}</option>)}
                  </select>
                </label>
                <label className="posModalField">
                  <span>Category</span>
                  <select className="posSelect" value={newProduct.category_id} onChange={(event) => updateNewProductTaxonomy("category_id", event.target.value)}>
                    <option value="">Unassigned</option>
                    {newProductCategories.map((row) => <option key={row.id} value={row.id}>{optionLabel(row)}</option>)}
                  </select>
                </label>
                <label className="posModalField">
                  <span>Subcategory</span>
                  <select className="posSelect" value={newProduct.subcategory_id} onChange={(event) => updateNewProductTaxonomy("subcategory_id", event.target.value)}>
                    <option value="">Unassigned</option>
                    {newProductSubcategories.map((row) => <option key={row.id} value={row.id}>{optionLabel(row)}</option>)}
                  </select>
                </label>
                <label className="posModalField">
                  <span>IVA</span>
                  <select className="posSelect" value={newProduct.vat_rate_percent} onChange={(event) => setNewProduct((current) => ({ ...current, vat_rate_percent: event.target.value }))}>
                    {VAT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>
            </div>

            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeCreateProductModal} disabled={creatingProduct}><FiX /> Cancel</button>
              <button type="submit" className="rm-topBtn rm-topBtn--green" disabled={creatingProduct}><FiPlus /> {creatingProduct ? "Creating" : "Create product"}</button>
            </div>
          </form>
        </div>
      ) : null}

      {showBulkEditModal && editorContext === access.contextKey ? (
        <div className="posModalOverlay">
          <div className="posTicketModal posProductModal posProductModal--wide" role="dialog" aria-modal="true" aria-label="Bulk edit POS products">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">Bulk edit products</h3>
                <div className="rm-rates-subtitle">{selectionLabel}</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeBulkEditModal} aria-label="Close bulk edit popup">
                x
              </button>
            </div>

            {error ? <div className="posStateBanner">{error}</div> : null}

            <div className="posProductModalBody">
              <div className="posBulkSelectionRow posBulkSelectionRow--modal">
                <div className="posBulkStatus">
                  <strong>{selectionLabel}</strong>
                  <span>{allFilteredSelected ? "Matching current filters" : "Manual selection"}</span>
                </div>
                <div className="rm-topActions">
                  <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={selectVisible} disabled={!products.length}>
                    <FiCheckSquare /> Visible
                  </button>
                  <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={selectAllFiltered} disabled={!count}>
                    <FiCheckSquare /> All filtered
                  </button>
                  <button type="button" className="rm-topBtn" onClick={clearSelection} disabled={!selectedCount}>
                    <FiX /> Clear
                  </button>
                </div>
              </div>

              <div className="posBulkGrid posBulkGrid--modal">
                <label className="posBulkField">
                  <span>Family</span>
                  <select
                    className="posSelect"
                    value={bulkPatch.family_id}
                    onChange={(event) =>
                      setBulkPatch((prev) => ({
                        ...prev,
                        family_id: event.target.value,
                        subfamily_id: BULK_NO_CHANGE,
                        category_id: BULK_NO_CHANGE,
                        subcategory_id: BULK_NO_CHANGE,
                      }))
                    }
                  >
                    <option value={BULK_NO_CHANGE}>No change</option>
                    <option value="">Clear family</option>
                    {taxonomy.families.map((row) => (
                      <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                    ))}
                  </select>
                </label>

                <label className="posBulkField">
                  <span>Subfamily</span>
                  <select
                    className="posSelect"
                    value={bulkPatch.subfamily_id}
                    onChange={(event) =>
                      setBulkPatch((prev) => ({
                        ...prev,
                        subfamily_id: event.target.value,
                        category_id: BULK_NO_CHANGE,
                        subcategory_id: BULK_NO_CHANGE,
                      }))
                    }
                  >
                    <option value={BULK_NO_CHANGE}>No change</option>
                    <option value="">Clear subfamily</option>
                    {bulkSubfamilies.map((row) => (
                      <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                    ))}
                  </select>
                </label>

                <label className="posBulkField">
                  <span>Category</span>
                  <select
                    className="posSelect"
                    value={bulkPatch.category_id}
                    onChange={(event) =>
                      setBulkPatch((prev) => ({
                        ...prev,
                        category_id: event.target.value,
                        subcategory_id: BULK_NO_CHANGE,
                      }))
                    }
                  >
                    <option value={BULK_NO_CHANGE}>No change</option>
                    <option value="">Clear category</option>
                    {bulkCategories.map((row) => (
                      <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                    ))}
                  </select>
                </label>

                <label className="posBulkField">
                  <span>Subcategory</span>
                  <select
                    className="posSelect"
                    value={bulkPatch.subcategory_id}
                    onChange={(event) => setBulkPatch((prev) => ({ ...prev, subcategory_id: event.target.value }))}
                  >
                    <option value={BULK_NO_CHANGE}>No change</option>
                    <option value="">Clear subcategory</option>
                    {bulkSubcategories.map((row) => (
                      <option key={row.id} value={row.id}>{optionLabel(row)}</option>
                    ))}
                  </select>
                </label>

                <label className="posBulkField">
                  <span>IVA</span>
                  <select
                    className="posSelect"
                    value={bulkPatch.vat_rate_percent}
                    onChange={(event) => setBulkPatch((prev) => ({ ...prev, vat_rate_percent: event.target.value }))}
                  >
                    <option value={BULK_NO_CHANGE}>No change</option>
                    <option value="">Clear IVA</option>
                    {VAT_OPTIONS.filter((option) => option.value !== "").map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeBulkEditModal} disabled={bulkSaving}>
                <FiX /> Cancel
              </button>
              <button
                type="button"
                className="rm-topBtn rm-topBtn--green"
                onClick={applyBulkEdit}
                disabled={!selectedCount || bulkSaving}
              >
                {bulkSaving ? "Applying" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
